import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Header from "./components/Header";
import Hero from "./components/Hero";
import ProductSection from "./components/ProductSection";
import LizWolfe from "./components/LizWolfe";
import BrandPromise from "./components/BrandPromise";
import Testimonials from "./components/Testimonials";
import Newsletter from "./components/Newsletter";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import ProductDrawer from "./components/ProductDrawer";
import DailyMultiFiber from "./components/DailyMultiFiber";
// Labor Day Sale unpublished — kept for reuse next year. To re-enable, restore this import:
// import LaborDaySale from "./components/LaborDaySale";
// July 4th Sale (unpublished — re-enable by restoring this import and the route below)
// import July4Sale from "./components/July4Sale";
// Memorial Day Sale (unpublished — re-enable by restoring this import and the route below)
// import MemorialDaySale from "./components/MemorialDaySale";
import { mockProducts, protocolInfo } from "./data/mock";
import productService from "./services/productService";
import cartService from "./services/cartService";

const LandingPage = () => {
  const [cart, setCart] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDrawerOpen, setProductDrawerOpen] = useState(false);
  const [products, setProducts] = useState({
    perimenopause: [],
    fitness: [],
    antiAging: [],
    mindMood: [],
    immuneSupport: [],
    digestiveBloodSugar: [],
    bodyRecomp: [],
    bodyRecompPhase2: []
  });
  const [loading, setLoading] = useState(true);

  // Fetch products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const data = await productService.getLandingPageProducts();
        
        const transformProduct = (p) => ({
          id: p.id,
          title: p.title,
          handle: p.handle,
          description: p.description || '',
          descriptionHtml: p.descriptionHtml || '',
          subtitle: p.subtitle || '',
          vendor: p.vendor || 'MD Logic Health',
          price: parseFloat(p.price || 0).toFixed(2),
          compareAtPrice: p.compareAtPrice ? parseFloat(p.compareAtPrice).toFixed(2) : null,
          image: p.image || 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=600&h=600&fit=crop',
          images: p.images || [p.image],
          rating: p.rating || 4.7,
          reviews: p.reviews || 50,
          tags: p.tags || [],
          variants: p.variants || [],
          sellingPlanGroups: p.sellingPlanGroups || [],
          hasSubscription: p.hasSubscription || false,
          variantId: p.variantId || ''
        });

        setProducts({
          perimenopause: (data.perimenopause || []).map(transformProduct),
          fitness: (data.fitness || []).map(transformProduct),
          antiAging: (data.antiAging || []).map(transformProduct),
          mindMood: (data.mindMood || []).map(transformProduct),
          immuneSupport: (data.immuneSupport || []).map(transformProduct),
          digestiveBloodSugar: (data.digestiveBloodSugar || []).map(transformProduct),
          bodyRecomp: (data.bodyRecomp || []).map(transformProduct),
          bodyRecompPhase2: (data.bodyRecompPhase2 || []).map(transformProduct)
        });
      } catch (err) {
        console.error('Failed to fetch products:', err);
        setProducts(mockProducts);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Scroll to URL hash section once products have rendered
  useEffect(() => {
    if (loading || !window.location.hash) return;
    const id = decodeURIComponent(window.location.hash.slice(1));
    const scroll = () => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    const timers = [setTimeout(scroll, 100), setTimeout(scroll, 700)];
    return () => timers.forEach(clearTimeout);
  }, [loading]);

  // Load cart on mount
  useEffect(() => {
    const loadCart = async () => {
      try {
        const existingCart = await cartService.getCart();
        if (existingCart) {
          setCart(existingCart);
        }
      } catch (err) {
        console.error('Failed to load cart:', err);
      }
    };
    loadCart();
  }, []);

  // Quick add to cart handler (from product card)
  const handleQuickAddToCart = useCallback(async (product) => {
    if (!product.variantId) {
      console.error('No variant ID for product:', product.title);
      return;
    }

    setCartLoading(true);
    try {
      const updatedCart = await cartService.addToCart(product.variantId, 1);
      setCart(updatedCart);
      setCartOpen(true);
    } catch (err) {
      console.error('Failed to add to cart:', err);
    } finally {
      setCartLoading(false);
    }
  }, []);

  // Add to cart with options (from product drawer)
  const handleAddToCartWithOptions = useCallback(async ({ variantId, quantity, sellingPlanId }) => {
    setCartLoading(true);
    try {
      const cartId = cartService.getCartId();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cartId,
          lines: [{ variantId, quantity, sellingPlanId }]
        })
      });
      
      if (!response.ok) throw new Error('Failed to add to cart');
      
      const updatedCart = await response.json();
      if (updatedCart.id) {
        cartService.setCartId(updatedCart.id);
      }
      setCart(updatedCart);
      setCartOpen(true);
    } catch (err) {
      console.error('Failed to add to cart:', err);
    } finally {
      setCartLoading(false);
    }
  }, []);

  // Handle product click
  const handleProductClick = useCallback((product) => {
    setSelectedProduct(product);
    setProductDrawerOpen(true);
  }, []);

  // Update cart quantity
  const handleUpdateQuantity = useCallback(async (lineId, quantity) => {
    setCartLoading(true);
    try {
      if (quantity === 0) {
        const updatedCart = await cartService.removeFromCart([lineId]);
        setCart(updatedCart);
      } else {
        const updatedCart = await cartService.updateCartLine(lineId, quantity);
        setCart(updatedCart);
      }
    } catch (err) {
      console.error('Failed to update quantity:', err);
    } finally {
      setCartLoading(false);
    }
  }, []);

  // Remove from cart
  const handleRemoveItem = useCallback(async (lineId) => {
    setCartLoading(true);
    try {
      const updatedCart = await cartService.removeFromCart([lineId]);
      setCart(updatedCart);
    } catch (err) {
      console.error('Failed to remove item:', err);
    } finally {
      setCartLoading(false);
    }
  }, []);

  // Checkout handler
  const handleCheckout = useCallback(async () => {
    setCartLoading(true);
    try {
      await cartService.checkout();
    } catch (err) {
      console.error('Failed to checkout:', err);
    } finally {
      setCartLoading(false);
    }
  }, []);

  // Cart count
  const cartCount = cart?.lines?.reduce((sum, line) => sum + line.quantity, 0) || 0;

  // Display products (use mock data as fallback during loading)
  const displayProducts = loading ? mockProducts : products;

  return (
    <div className="min-h-screen bg-white">
      <Header 
        cartCount={cartCount} 
        onCartClick={() => setCartOpen(true)} 
      />
      
      <main>
        <Hero />
        
        <LizWolfe />
        
        <ProductSection
          id="perimenopause"
          title={protocolInfo.perimenopause.name}
          tagline={protocolInfo.perimenopause.tagline}
          description={protocolInfo.perimenopause.description}
          products={displayProducts.perimenopause}
          accentColor={protocolInfo.perimenopause.color}
          onAddToCart={handleQuickAddToCart}
          onProductClick={handleProductClick}
          loading={loading}
        />
        
        <ProductSection
          id="fitness"
          title={protocolInfo.fitness.name}
          tagline={protocolInfo.fitness.tagline}
          description={protocolInfo.fitness.description}
          products={displayProducts.fitness}
          accentColor={protocolInfo.fitness.color}
          onAddToCart={handleQuickAddToCart}
          onProductClick={handleProductClick}
          loading={loading}
        />
        
        <ProductSection
          id="anti-aging"
          title={protocolInfo.antiAging.name}
          tagline={protocolInfo.antiAging.tagline}
          description={protocolInfo.antiAging.description}
          products={displayProducts.antiAging}
          accentColor={protocolInfo.antiAging.color}
          onAddToCart={handleQuickAddToCart}
          onProductClick={handleProductClick}
          loading={loading}
        />
        
        <ProductSection
          id="mind-mood"
          title={protocolInfo.mindMood.name}
          tagline={protocolInfo.mindMood.tagline}
          description={protocolInfo.mindMood.description}
          products={displayProducts.mindMood}
          accentColor={protocolInfo.mindMood.color}
          onAddToCart={handleQuickAddToCart}
          onProductClick={handleProductClick}
          loading={loading}
        />
        
        <ProductSection
          id="immune-support"
          title={protocolInfo.immuneSupport.name}
          tagline={protocolInfo.immuneSupport.tagline}
          description={protocolInfo.immuneSupport.description}
          products={displayProducts.immuneSupport}
          accentColor={protocolInfo.immuneSupport.color}
          onAddToCart={handleQuickAddToCart}
          onProductClick={handleProductClick}
          loading={loading}
        />
        
        <ProductSection
          id="digestive-blood-sugar"
          title={protocolInfo.digestiveBloodSugar.name}
          tagline={protocolInfo.digestiveBloodSugar.tagline}
          description={protocolInfo.digestiveBloodSugar.description}
          products={displayProducts.digestiveBloodSugar}
          accentColor={protocolInfo.digestiveBloodSugar.color}
          onAddToCart={handleQuickAddToCart}
          onProductClick={handleProductClick}
          loading={loading}
        />
        
        <ProductSection
          id="body-recomp-phase-1"
          title={protocolInfo.bodyRecomp.name}
          tagline={protocolInfo.bodyRecomp.tagline}
          description={protocolInfo.bodyRecomp.description}
          products={displayProducts.bodyRecomp}
          accentColor={protocolInfo.bodyRecomp.color}
          onAddToCart={handleQuickAddToCart}
          onProductClick={handleProductClick}
          loading={loading}
        />
        
        <ProductSection
          id="body-recomp-phase-2"
          title={protocolInfo.bodyRecompPhase2.name}
          tagline={protocolInfo.bodyRecompPhase2.tagline}
          description={protocolInfo.bodyRecompPhase2.description}
          products={displayProducts.bodyRecompPhase2}
          accentColor={protocolInfo.bodyRecompPhase2.color}
          onAddToCart={handleQuickAddToCart}
          onProductClick={handleProductClick}
          loading={loading}
        />
        
        <BrandPromise />
        
        <Testimonials />
        
        <Newsletter />
      </main>
      
      <Footer />

      {/* Product Drawer */}
      <ProductDrawer
        isOpen={productDrawerOpen}
        onClose={() => setProductDrawerOpen(false)}
        product={selectedProduct}
        onAddToCart={handleAddToCartWithOptions}
        loading={cartLoading}
      />

      {/* Cart Drawer */}
      <CartDrawer
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
        onCheckout={handleCheckout}
        loading={cartLoading}
      />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/fiber" element={<DailyMultiFiber />} />
          {/* Labor Day Sale route — unpublished, kept for reuse next year. To re-enable, restore:
              <Route path="/labor-day-sale" element={<LaborDaySale />} /> */}
          {/* July 4th Sale route — unpublished. To re-enable, restore the import above and uncomment:
              <Route path="/july-4-sale" element={<July4Sale />} /> */}
          {/* Memorial Day Sale route — unpublished. To re-enable, restore the import above and uncomment:
              <Route path="/memorial-day-sale" element={<MemorialDaySale />} /> */}
          {/* Catch-all: any unknown URL falls back to the landing page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
